import { MmMonitoringResult, MmPhoto, MmPriceCheckerRecord } from '../types/domain';
import { MmOutcome, MmReviewStatus } from '../types/enums';

/** Ключей со значением null в ответе нет вовсе — поэтому все поля необязательные */
export interface MmWireResultPosition {
  id?: string;
  trading_network_id?: string;
  sub_task_id?: string;
  monitored_at?: string;
  uploaded_at?: string;
  uploaded_by?: string;
  search_criterion_snapshot_id?: string;
  searched_client_product_snapshot_id?: string;
  searched_competitor_product_snapshot_id?: string;
  has_resulting_monitored_product?: boolean;
  was_searching_product_skipped?: boolean;
  searching_product_skip_reason?: string;
  auditor_comment?: string;
  auditorComment?: string;
  admin_comment?: string;
  status?: string;
  geo_latitude?: number | string;
  geoLatitude?: number | string;
  geo_longitude?: number | string;
  geoLongitude?: number | string;
  geo_accuracy?: number | string;
  geoAccuracy?: number | string;
  geo_altitude?: number | string;
  geoAltitude?: number | string;
  was_product_recognized?: boolean;
  wasProductRecognized?: boolean;
  /** Приходит строчными буквами; отсутствие или null означает pending */
  review_status?: string;
  reviewStatus?: string;
  has_linked_photos?: boolean;
  resulting_products?: MmWireResultProduct[];
  photos?: MmWireResultPhoto[];
  price_checker_records?: MmWirePriceCheckerRecord[];
  priceCheckerRecords?: MmWirePriceCheckerRecord[];
}

export interface MmWireResultProduct {
  id?: string;
  sub_task_id?: string;
  task_position_id?: string;
  bar_code?: string;
  regular_price?: number | string;
  promo_price?: number | string;
  price_source?: string;
  volume?: number | string;
  measurement_unit?: string;
  measurementUnit?: string;
  was_recognized_automatically?: boolean;
  recognition_confidence_level?: number | string;

  /** Поля объявлены в двух регистрах: byIds отдаёт snake_case, а поиск результатов — camelCase */
  recognized_name?: string;
  recognizedName?: string;
  card_price?: number | string;
  cardPrice?: number | string;
  wholesale_price?: number | string;
  wholesalePrice?: number | string;
  wholesale_volume?: number | string;
  wholesaleVolume?: number | string;
  competitor_sku?: string;
  competitorSku?: string;
  promo_type?: string;
  promoType?: string;
  currency?: string;
  recognized_vgh?: string;
  recognizedVgh?: string;
  confidence_promo?: number | string;
  confidencePromo?: number | string;
  confidence_name?: number | string;
  confidenceName?: number | string;
  confidence_barcode?: number | string;
  confidenceBarcode?: number | string;
  confidence_vgh?: number | string;
  confidenceVgh?: number | string;
  was_patched_manually?: boolean;
  patched_at?: string;
  patched_by_user_id?: string;
  uploaded_by_user_id?: string;
  uploaded_by_device_id?: string;

  is_analog?: boolean;

  /** Эти восемь полей бэкенд не заполняет никогда — строить на них группировку и названия нельзя */
  group_tag_id?: string;
  category_tag_id?: string;
  subcategory_tag_id?: string;
  group_name?: string;
  category_name?: string;
  subcategory_name?: string;
  client_internal_code?: string;
  client_sku_name?: string;
}

export interface MmWireResultPhoto {
  id?: string;
  task_position_id?: string;
  /** Не подставлять в <img src>: кадр запрашивается через fetchPhoto с заголовком авторизации */
  url?: string;
  /** @deprecated Уходит из ответа; ключ хранилища, не адрес для браузера. */
  storage_link?: string;
  shot_by_user_id?: string;
  shot_by_device_id?: string;
  thumbnailUrl?: string | null;
  /** @deprecated Устаревшее написание ключа; читаем как запасной вариант */
  thumbnail_url?: string;
  shotAt?: string;
  /** @deprecated На случай snake_case у вложенного фото. */
  shot_at?: string;
  /** @deprecated Уходит из ответа вместе с `content`. */
  content_type?: string;
  /** @deprecated Байты снимка в Base64 уходят из ответа. */
  content?: string;
}

export interface MmWirePriceCheckerRecord {
  id?: string;
  price?: number | string;
  promoPrice?: number | string;
  promo_price?: number | string;
  capturedAt?: string;
  captured_at?: string;
  status?: string;
  photoUrl?: string;
  photo_url?: string;
  skipPhotoUrl?: string;
  skip_photo_url?: string;
  currency?: string;
  geoLatitude?: number | string;
  geo_latitude?: number | string;
  geoLongitude?: number | string;
  geo_longitude?: number | string;
  geoAccuracy?: number | string;
  geo_accuracy?: number | string;
  confidence?: number | string;
  skipReason?: string;
  skip_reason?: string;
  skipComment?: string;
  skip_comment?: string;
  wasEdited?: boolean;
  was_edited?: boolean;
  originalPrice?: number | string;
  original_price?: number | string;
  editedPrice?: number | string;
  edited_price?: number | string;
}

/** Коды бэкенда и фронта расходятся не только регистром — нужна таблица, а не toLowerCase */
export const MM_BE_SKIP_REASON_TO_OUTCOME: { [key: string]: string } = {
  NOT_FOUND: MmOutcome.PRODUCT_NOT_FOUND,
  PRODUCT_NOT_FOUND: MmOutcome.PRODUCT_NOT_FOUND,
  product_not_found: MmOutcome.PRODUCT_NOT_FOUND,
  OUT_OF_STOCK: MmOutcome.OUT_OF_STOCK,
  out_of_stock: MmOutcome.OUT_OF_STOCK,
  NO_PRICE_TAG: MmOutcome.NO_PRICE_TAG,
  no_price_tag: MmOutcome.NO_PRICE_TAG,
  OTHER: MmOutcome.SKIPPED,
  other: MmOutcome.SKIPPED,
};

/** Стадия исполнения позиции аудитором, а не приёмка администратором */
export const MM_BE_POSITION_STATUS_PENDING = 'PENDING';
export const MM_BE_POSITION_STATUS_DONE = 'DONE';
export const MM_BE_POSITION_STATUS_SKIPPED = 'SKIPPED';

function readText(value: any): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  const text = String(value).trim();

  return text !== '' ? text : null;
}

function readNumber(value: any): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = typeof value === 'number' ? value : Number(String(value).trim());

  return isFinite(parsed) ? parsed : null;
}

function pickText(source: any, snakeKey: string, camelKey: string): string | null {
  if (!source) {
    return null;
  }

  const snake = readText(source[snakeKey]);

  return snake !== null ? snake : readText(source[camelKey]);
}

function pickNumber(source: any, snakeKey: string, camelKey: string): number | null {
  if (!source) {
    return null;
  }

  const snake = readNumber(source[snakeKey]);

  return snake !== null ? snake : readNumber(source[camelKey]);
}

function pickBool(source: any, snakeKey: string, camelKey: string): boolean | null {
  if (!source) {
    return null;
  }

  const snake = readBool(source[snakeKey]);

  return snake !== null ? snake : readBool(source[camelKey]);
}

/** Код приходит и в виде other, и в виде OTHER — карта покрывает оба написания */
function lookupSkipReasonOutcome(reason: string): string | undefined {
  const direct = MM_BE_SKIP_REASON_TO_OUTCOME[reason];

  if (direct !== undefined) {
    return direct;
  }

  const upper = reason.toUpperCase();

  return upper !== reason ? MM_BE_SKIP_REASON_TO_OUTCOME[upper] : undefined;
}

function readReviewStatusWire(position: MmWireResultPosition): MmReviewStatus {
  const snake = readText(position.review_status);
  const raw = snake !== null ? snake : readText(position.reviewStatus);

  if (raw === null) {
    return MmReviewStatus.PENDING;
  }

  const lower = raw.toLowerCase();

  if (lower === MmReviewStatus.ACCEPTED) {
    return MmReviewStatus.ACCEPTED;
  }

  if (lower === MmReviewStatus.REJECTED) {
    return MmReviewStatus.REJECTED;
  }

  if (lower === MmReviewStatus.PRICE_CORRECTED) {
    return MmReviewStatus.PRICE_CORRECTED;
  }

  if (lower === MmReviewStatus.PENDING) {
    return MmReviewStatus.PENDING;
  }

  return MmReviewStatus.PENDING;
}

/**
 * Чип причины только когда исход схлопнут в skipped (wire `other`).
 * Остальные причины уже стали кодом исхода — дублировать их в чипе нельзя.
 */
function skipReasonForChip(position: MmWireResultPosition): string | null {
  const reason = readText(position.searching_product_skip_reason);

  if (reason === null) {
    return null;
  }

  const mapped = lookupSkipReasonOutcome(reason);

  if (mapped !== MmOutcome.SKIPPED) {
    return null;
  }

  return 'other';
}

function readBool(value: any): boolean | null {
  if (value === null || value === undefined) {
    return null;
  }

  return value === true || value === 'true';
}

function toConfidencePercent(value: number | null): number | null {
  if (value === null) {
    return null;
  }

  return value <= 1 ? value * 100 : value;
}

function readAnalogFlag(product: MmWireResultProduct): boolean | null {
  if (product === null || product === undefined) {
    return null;
  }

  return readBool(product.is_analog);
}

/** Ручка отдаёт голый массив: readPage разобрал бы его в тихо пустую страницу */
export function readResultPositions(data: any): MmWireResultPosition[] {
  if (Array.isArray(data)) {
    return data as MmWireResultPosition[];
  }

  if (data && Array.isArray(data.content)) {
    return data.content as MmWireResultPosition[];
  }

  if (data && Array.isArray(data.items)) {
    return data.items as MmWireResultPosition[];
  }

  return [];
}

/** Сырых признаков бэкенд не отдаёт: кладём готовый код, шкалу исходов здесь не дублируем */
export function deriveOutcomeCode(
  position: MmWireResultPosition,
  price: number | null,
): string {
  if (position === null || position === undefined) {
    return MmOutcome.UNSPECIFIED;
  }

  const status = readText(position.status);

  if (readBool(position.was_searching_product_skipped) === true
      || status === MM_BE_POSITION_STATUS_SKIPPED) {
    const reason = readText(position.searching_product_skip_reason);

    if (reason !== null) {
      const mapped = lookupSkipReasonOutcome(reason);

      /** Незнакомую причину пропуска проносим сырой: перечень исходов не закрыт, схлопывать нельзя */
      return mapped !== undefined ? mapped : reason;
    }

    return MmOutcome.SKIPPED;
  }

  if (status === MM_BE_POSITION_STATUS_PENDING) {
    return MmOutcome.NOT_DONE;
  }

  const hasProduct = readBool(position.has_resulting_monitored_product) === true
    || (position.resulting_products !== undefined
        && position.resulting_products !== null
        && position.resulting_products.length > 0);

  if (hasProduct) {
    return price !== null ? MmOutcome.PRICE_CAPTURED : MmOutcome.PRICE_NOT_RECOGNIZED;
  }

  return MmOutcome.UNSPECIFIED;
}

/** Строка таблицы одна: ведущим берём первый не-аналог, остальные товары позиции теряются */
function pickPrimaryProduct(products: MmWireResultProduct[] | undefined): MmWireResultProduct | null {
  if (products === null || products === undefined || products.length === 0) {
    return null;
  }

  for (let i = 0; i < products.length; i += 1) {
    if (readAnalogFlag(products[i]) !== true) {
      return products[i];
    }
  }

  return products[0];
}

function mapPhotos(
  photos: MmWireResultPhoto[] | undefined,
  fallbackCapturedAt: string | null,
): MmPhoto[] {
  if (photos === null || photos === undefined || photos.length === 0) {
    return [];
  }

  const result: MmPhoto[] = [];

  for (let i = 0; i < photos.length; i += 1) {
    const photo = photos[i];
    const id = readText(photo.id);

    if (id === null) {
      continue;
    }

    const apiUrl = readText(photo.url);
    const thumbnailCamel = readText(photo.thumbnailUrl);
    const thumbnailUrl = thumbnailCamel !== null ? thumbnailCamel : readText(photo.thumbnail_url);
    const shotCamel = readText(photo.shotAt);
    const shotAt = shotCamel !== null ? shotCamel : readText(photo.shot_at);

    result.push({
      id: id,
      /** Путь кадра относительный: галерея грузит его через fetchPhoto с авторизацией, а не в <img src> */
      url: apiUrl !== null ? apiUrl : '',
      thumbnailUrl: thumbnailUrl,
      capturedAt: shotAt !== null ? shotAt : fallbackCapturedAt,
    });
  }

  return result;
}

function mapPriceCheckerRecords(position: MmWireResultPosition): MmPriceCheckerRecord[] {
  const snake = position.price_checker_records;
  const camel = position.priceCheckerRecords;
  const raw: MmWirePriceCheckerRecord[] =
    snake && Array.isArray(snake) ? snake : (camel && Array.isArray(camel) ? camel : []);
  const result: MmPriceCheckerRecord[] = [];

  for (let i = 0; i < raw.length; i += 1) {
    const rec = raw[i];
    const id = rec ? readText(rec.id) : null;

    if (id === null) {
      continue;
    }

    const capturedCamel = rec ? readText(rec.capturedAt) : null;
    const capturedAt = capturedCamel !== null ? capturedCamel : (rec ? readText(rec.captured_at) : null);
    const promoCamel = rec ? readNumber(rec.promoPrice) : null;
    const promoPrice = promoCamel !== null ? promoCamel : (rec ? readNumber(rec.promo_price) : null);
    const photoCamel = rec ? readText(rec.photoUrl) : null;
    const photoUrl = photoCamel !== null ? photoCamel : (rec ? readText(rec.photo_url) : null);
    const skipCamel = rec ? readText(rec.skipPhotoUrl) : null;
    const skipPhotoUrl = skipCamel !== null ? skipCamel : (rec ? readText(rec.skip_photo_url) : null);

    result.push({
      id: id,
      price: rec ? readNumber(rec.price) : null,
      promoPrice: promoPrice,
      capturedAt: capturedAt,
      status: rec ? readText(rec.status) : null,
      photoUrl: photoUrl,
      skipPhotoUrl: skipPhotoUrl,
      currency: rec ? readText(rec.currency) : null,
      geoLatitude: pickNumber(rec, 'geo_latitude', 'geoLatitude'),
      geoLongitude: pickNumber(rec, 'geo_longitude', 'geoLongitude'),
      geoAccuracy: pickNumber(rec, 'geo_accuracy', 'geoAccuracy'),
      confidence: toConfidencePercent(rec ? readNumber(rec.confidence) : null),
      skipReason: pickText(rec, 'skip_reason', 'skipReason'),
      skipComment: pickText(rec, 'skip_comment', 'skipComment'),
      wasEdited: pickBool(rec, 'was_edited', 'wasEdited'),
      originalPrice: pickNumber(rec, 'original_price', 'originalPrice'),
      editedPrice: pickNumber(rec, 'edited_price', 'editedPrice'),
    });
  }

  return result;
}

export function mapPositionToResult(position: MmWireResultPosition): MmMonitoringResult | null {
  if (position === null || position === undefined) {
    return null;
  }

  const id = readText(position.id);

  if (id === null) {
    return null;
  }

  const product = pickPrimaryProduct(position.resulting_products);
  const price = product !== null ? readNumber(product.regular_price) : null;
  const promoPrice = product !== null ? readNumber(product.promo_price) : null;

  /** sub_task_id кладётся в taskId намеренно: id модели — это подзадача, само задание в batchId */
  const subTaskId = readText(position.sub_task_id);

  const result: MmMonitoringResult = {
    id: id,
    taskId: subTaskId !== null ? subTaskId : '',

    taskClassificationId: null,
    taskProductId: readText(position.searched_client_product_snapshot_id),

    price: price,
    promoPrice: promoPrice,
    outcome: deriveOutcomeCode(position, price),

    skipReason: skipReasonForChip(position),

    competitorProductName: product !== null ? readText(product.client_sku_name) : null,
    isAnalog: product !== null ? readAnalogFlag(product) : null,

    reviewStatus: readReviewStatusWire(position),
    correctedPrice: null,

    capturedAt: readText(position.monitored_at),
    receivedAt: readText(position.uploaded_at),

    auditorId: readText(position.uploaded_by),
    auditorLogin: null,

    photos: mapPhotos(position.photos, readText(position.monitored_at)),

    priceSource: product !== null ? readText(product.price_source) : null,

    confidenceLevel: product !== null
      ? toConfidencePercent(readNumber(product.recognition_confidence_level))
      : null,
    promoTypeId: null,

    /** Сырые признаки исхода оставляем null, иначе они отбросят уже вычисленный outcome */
    wasProductRecognized: pickBool(
      position,
      'was_product_recognized',
      'wasProductRecognized',
    ),
    priceCheckerRecords: mapPriceCheckerRecords(position),

    /** recognizedName — прочитанное с ценника конкурента, а competitorProductName — плановый товар */
    recognizedName: pickText(product, 'recognized_name', 'recognizedName'),
    cardPrice: pickNumber(product, 'card_price', 'cardPrice'),
    wholesalePrice: pickNumber(product, 'wholesale_price', 'wholesalePrice'),
    wholesaleVolume: pickText(product, 'wholesale_volume', 'wholesaleVolume'),
    competitorSku: pickText(product, 'competitor_sku', 'competitorSku'),
    promoType: pickText(product, 'promo_type', 'promoType'),
    currency: product !== null ? readText(product.currency) : null,
    recognizedVgh: pickText(product, 'recognized_vgh', 'recognizedVgh'),
    productVolume: product !== null ? readNumber(product.volume) : null,
    measurementUnit: pickText(product, 'measurement_unit', 'measurementUnit'),

    confidencePromo: toConfidencePercent(pickNumber(product, 'confidence_promo', 'confidencePromo')),
    confidenceName: toConfidencePercent(pickNumber(product, 'confidence_name', 'confidenceName')),
    confidenceBarcode: toConfidencePercent(
      pickNumber(product, 'confidence_barcode', 'confidenceBarcode'),
    ),
    confidenceVgh: toConfidencePercent(pickNumber(product, 'confidence_vgh', 'confidenceVgh')),

    geoLatitude: pickNumber(position, 'geo_latitude', 'geoLatitude'),
    geoLongitude: pickNumber(position, 'geo_longitude', 'geoLongitude'),
    geoAccuracy: pickNumber(position, 'geo_accuracy', 'geoAccuracy'),
    geoAltitude: pickNumber(position, 'geo_altitude', 'geoAltitude'),

    auditorComment: pickText(position, 'auditor_comment', 'auditorComment'),
  };

  const barCode = product !== null ? readText(product.bar_code) : null;

  if (barCode !== null) {
    (result as any).barCode = barCode;
  }

  return result;
}

export function mapResultsResponse(data: any): MmMonitoringResult[] {
  const positions = readResultPositions(data);
  const rows: MmMonitoringResult[] = [];

  for (let i = 0; i < positions.length; i += 1) {
    const row = mapPositionToResult(positions[i]);

    if (row !== null) {
      rows.push(row);
    }
  }

  return rows;
}

/** Путь задан относительно базового пути MM_API_BASE_PATH */
export const MM_RESULTS_BY_IDS_PATH = '/monitoring/tasks/results/byIds';

function distinctIds(source: string[] | null | undefined): string[] {
  if (source === null || source === undefined) {
    return [];
  }

  const seen: { [key: string]: boolean } = {};
  const result: string[] = [];

  for (let i = 0; i < source.length; i += 1) {
    const value = readText(source[i]);

    if (value !== null && seen[value] !== true) {
      seen[value] = true;
      result.push(value);
    }
  }

  return result;
}

/** Оба списка пусты — возвращается null: такой запрос слать нельзя, бэкенд ответит 422 */
export function buildResultsByIdsParams(
  taskIds: string[] | null,
  subTaskIds: string[] | null,
): { [key: string]: string } | null {
  const tasks = distinctIds(taskIds);
  const subTasks = distinctIds(subTaskIds);

  if (tasks.length === 0 && subTasks.length === 0) {
    return null;
  }

  const params: { [key: string]: string } = {};

  if (tasks.length > 0) {
    params.taskIds = tasks.join(',');
  }

  if (subTasks.length > 0) {
    params.subTaskIds = subTasks.join(',');
  }

  return params;
}
